/*
 * Nominatim-powered address autocomplete (no API key required).
 * Debounces input, queries OpenStreetMap Nominatim, shows suggestions,
 * and reports both formatted_address and precise lat/lng on selection.
 *
 * Props:
 *   value          string (controlled address text)
 *   onChange       (newText: string) => void
 *   onPlace        (place: { formatted_address, lat, lng, raw }) => void
 *   testid         data-testid for the input
 *   placeholder    string
 *   className      optional extra classes for the input
 *   countryCodes   comma-separated ISO codes, default "us"
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";

const NOMINATIM = "https://nominatim.openstreetmap.org/search";

export default function AddressAutocomplete({
  value,
  onChange,
  onPlace,
  testid,
  placeholder = "Start typing an address…",
  className = "",
  countryCodes = "us",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (!q || q.trim().length < 3) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const url = `${NOMINATIM}?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=${countryCodes}`;
        // Nominatim requests a descriptive User-Agent, but browsers forbid setting it;
        // the Referer + default UA is acceptable for low-volume interactive use.
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        const data = await res.json();
        if (lastQueryRef.current !== q) return; // stale
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [countryCodes],
  );

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setVerified(false);
    lastQueryRef.current = v;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 350);
  };

  const pick = (s) => {
    const formatted = s.display_name;
    const lat = parseFloat(s.lat);
    const lng = parseFloat(s.lon);
    onChange(formatted);
    onPlace?.({ formatted_address: formatted, lat, lng, raw: s });
    setSuggestions([]);
    setOpen(false);
    setVerified(true);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
        className={`w-full pl-9 pr-9 border border-[#E2E0D8] rounded-md px-3 py-2 text-sm ${className}`}
      />
      {loading && <Loader2 size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      {!loading && verified && <CheckCircle2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" />}
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-[10000] left-0 right-0 mt-1 bg-white border border-[#E2E0D8] rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={testid ? `${testid}-suggestions` : "address-suggestions"}
        >
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s.place_id}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 ${
                i === highlight ? "bg-slate-100" : "hover:bg-slate-50"
              }`}
              data-testid={testid ? `${testid}-suggestion-${i}` : undefined}
            >
              <div className="text-slate-900 truncate">{s.display_name}</div>
              <div className="text-[11px] text-slate-500">{s.type} · {parseFloat(s.lat).toFixed(4)}, {parseFloat(s.lon).toFixed(4)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
